<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="800px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        style="max-width:36px !important"
        icon
        @click.stop=""
        v-on="on"
      >
        <v-icon color="hrissecondary">
          mdi-plus-circle
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Branch"
      text="Create Branch"
      class="mb-0"
      :loading="loading"
    > 
      <form v-if="!loading">
        <v-container>
          <v-row>
            <v-col cols="6">
              <v-row no-gutters>
                <v-col cols="12">
                  <v-text-field
                    v-model="branchName"
                    v-validate="'required'"
                    :error-messages="errors.collect('branchName')"
                    data-vv-name="branchName"
                    data-vv-as="branch name"
                    label="Branch Name"
                    outlined
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="contactNumber"
                    v-validate="'phoneLength'"
                    v-mask="['###-########']"
                    :error-messages="errors.collect('contactNumber')"
                    data-vv-name="contactNumber"
                    label="Contact Number"
                    :disabled="!branchName"
                    :filled="!branchName"
                    outlined
                  />
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="6">
              <v-row no-gutters>
                <v-col cols="12">
                  <v-text-field
                    v-model="addressLine1"
                    v-validate="branchName?'required':''"
                    :error-messages="errors.collect('addressLine1')"
                    data-vv-name="addressLine1"
                    data-vv-as="address"
                    :disabled="!branchName"
                    :filled="!branchName"
                    label="Address Line 1"
                    outlined
                  />
                </v-col>
                
                <v-col cols="12">
                  <v-text-field
                    v-model="addressLine2"
                    :disabled="!addressLine1"
                    :filled="!addressLine1"
                    label="Address Line 2"
                    outlined
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="addressLine3"
                    :disabled="!addressLine1"
                    :filled="!addressLine1"
                    label="Address Line 3"
                    outlined
                  />
                </v-col>

                <v-col cols="12">
                  <v-autocomplete
                    v-model="postcode"
                    v-validate="'required'"
                    :items="postcodes"
                    item-text="postcode"
                    item-value="postcode"
                    value="postcode"
                    :error-messages="errors.collect('postcode')"
                    data-vv-name="postcode"
                    data-vv-as="postcode"
                    label="Postcode"
                    outlined
                    @input="postCodeOnChanged(postcode)"
                  />
                </v-col>

                <v-col cols="12">
                  <v-autocomplete
                    v-model="city"
                    v-validate="branchName?'required':''"
                    :disabled="dropdownDisabled"
                    :filled="!postcode"
                    :items="filteredCities"
                    :error-messages="errors.collect('city')"
                    data-vv-name="city"
                    data-vv-as="city"
                    item-text="name"
                    item-value="id"
                    value="id"
                    label="City"
                    hide-selected
                    outlined
                    @input="cityOnChanged(city)"
                  />
                </v-col>

                <v-col cols="12">
                  <v-autocomplete
                    v-model="state"
                    v-validate="branchName?'required':''"
                    :error-messages="errors.collect('state')"
                    data-vv-name="state"
                    data-vv-as="state"
                    disabled
                    :filled="!city"
                    :items="states"
                    item-text="name"
                    item-value="id"
                    value="id"
                    label="State"
                    hide-selected
                    outlined
                  />
                </v-col>

                <v-col cols="12">
                  <v-autocomplete
                    v-model="country"
                    v-validate="branchName?'required':''"
                    :error-messages="errors.collect('country')"
                    data-vv-name="country"
                    data-vv-as="country"
                    :items="countries"
                    disabled
                    :filled="!state"
                    item-text="name"
                    item-value="id"
                    value="id"
                    label="Country"
                    hide-selected
                    outlined
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </form>
      
      <v-card-actions v-if="!loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"  
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary" 
          class="white--text px-10"        
          @click="submitForm"
        >
          Create
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      companyId: {
        type: String,
        required: true,
      },
    },
    data () {
      return {
        progressing: false,
        dialog: null,
        branchName: null,
        contactNumber: null,
        loading: false,
        countries: [],
        states: [],
        postcodes: [],
        cities: [],
        addressLine1: null,
        addressLine2: null,
        addressLine3: null,
        state: null,
        city: null,
        country: null,
        postcode: null,
        dropdownDisabled: null,
        cityIds: [],           
      }
    },
    computed: {
      filteredCities () {
        return this.cities.filter(city => {
          return this.cityIds.includes(city.id)
        })         
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) return this.$resetData()

        this.loading = true

        this.$validator.extend('phoneLength', {
          getMessage (field, args) {
            return 'Invalid phone number (at least 9 numbers and max 11)'
          },
          validate (value, args) {
            return value.toString().length >= 10 && value.toString().length <= 12
          },
        })

        Promise.all([
          this.$api.countries().list(),
          this.$api.states().list(),
          this.$api.cities().list(),
          this.$api.postcodes().list(),
        ])
          .then(([countries, states, cities, postcodes]) => {
            this.countries = countries.data
            this.states = states.data
            this.cities = cities.data
            this.postcodes = _.orderBy(postcodes.data, ['postcode'], ['asc'])

            this.loading = false
          })
      },
    },
    methods: { 
      prepareFormData () {
        return {
          company_uid: this.companyId,
          branch_name: this.branchName,
          branch_contact_no: this.contactNumber,
          address_line_one: this.addressLine1,
          address_line_two: this.addressLine2,
          address_line_three: this.addressLine3,
          postcode: this.postcode,
          city_id: this.city,
          state_id: this.state,
          country_id: this.country,
          chat_id: '2631a652-595f-4568-a95c-f8d5efd744a1',
        }
      },   
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 

            this.progressing = true
    
            this.$api.hrisBranches().create(this.prepareFormData())
              .then((res) => {
                if (res.status === 200 || res.status === 201) {
                  this.$emit('created', res.data)                
                  this.$snack({ message: 'Added new branch' })

                  this.dialog = false               
                }
              })
              .catch((err) => {
                if (err.response.status === 500) {              
                  this.$snack({ 
                    color: 'error',
                    message: err.response.data.status, 
                  })
                }            
            
                this.progressing = false
              })
              .then(() => {
                this.progressing = false
              })
          })
      },

      postCodeOnChanged (postcodeValue) {
        this.city = null
        this.state = null
        this.country = null
        var cityID = this.postcodes.filter(p => {
          return p.postcode === postcodeValue
        })  

        this.cityIds = this.cities.filter(city => {
          return cityID.some(city2 => {
            return city2.city_id === city.id
          })
        }).map(city3 => {
          return city3.id
        })

        if (this.cityIds.length > 1) {
          this.dropdownDisabled = false
        } else {
          this.dropdownDisabled = true

          let cityId = (this.postcodes.find(postcode => {
            return postcode.postcode === postcodeValue
          }) || {}).city_id

          if (cityId) {
            var cityObject = this.getCity(cityId)
            if (cityObject) {
              this.city = cityObject.id
              var stateObject = this.getState(cityObject.state_id)
              
              if (stateObject) {
                this.state = stateObject.id
                var countryObject = this.getCountry(stateObject.country_id)
                this.country = countryObject.id
              }
            }
          }
        }
      },
      cityOnChanged (cityId) {
        var cityObject = this.getCity(cityId)
        if (cityObject) {
          var stateObject = this.getState(cityObject.state_id)
          
          if (stateObject) {
            this.state = stateObject.id
            var countryObject = this.getCountry(stateObject.country_id)
            this.country = countryObject.id
          }
        }
      },
      getCity (cityId) {
        if (isNaN(parseInt(cityId))) return null
        return this.cities.find(city => {
          return city.id === parseInt(cityId)
        })
      },
      getState (stateId) {
        if (isNaN(parseInt(stateId))) return null
        return this.states.find(state => {
          return state.id === parseInt(stateId)
        })
      },
      getCountry (countryId) {
        if (isNaN(parseInt(countryId))) return null
        return this.countries.find(country => {
          return country.id === parseInt(countryId)
        })
      },
  
    },
  }
</script>
